import { useEffect, useRef } from "react";
import { apiClient, type Api } from "../../services/api";

type PersonalNotification =
  Api["get"]["/webapp/private/notification/personals"]["output"]["body"][number];
type BroadcastNotification =
  Api["get"]["/webapp/private/notification/broadcasts"]["output"]["body"][number];

type LoadedQuery<T> =
  { state: "idle" | "pending" | "error" } | { state: "success"; body: T[] };

/**
 * Fires mark-as-read API calls once when both notification queries succeed.
 * Extracted from NotificationCenter (business component rule bans useEffect).
 */
export function useMarkNotificationsAsRead(
  personals: LoadedQuery<PersonalNotification>,
  broadcasts: LoadedQuery<BroadcastNotification>
) {
  const hasMarkedAsRead = useRef(false);

  useEffect(() => {
    if (
      personals.state !== "success" ||
      broadcasts.state !== "success" ||
      hasMarkedAsRead.current
    )
      return;

    const unreadPersonalIds = personals.body
      .filter((n) => !n.read)
      .map((n) => n.id);
    const unreadBroadcastIds = broadcasts.body
      .filter((n) => !n.read)
      .map((n) => n.id);

    if (unreadPersonalIds.length === 0 && unreadBroadcastIds.length === 0)
      return;

    hasMarkedAsRead.current = true;

    const requests = [];
    if (unreadPersonalIds.length > 0)
      requests.push(
        apiClient.post("/webapp/private/notification/personals/mark-as-read", {
          json: { ids: unreadPersonalIds },
        })
      );
    if (unreadBroadcastIds.length > 0)
      requests.push(
        apiClient.post("/webapp/private/notification/broadcasts/mark-as-read", {
          json: { ids: unreadBroadcastIds },
        })
      );

    void Promise.all(requests);
  }, [personals, broadcasts]);
}
